// Modal shown after Facebook OAuth when the user manages multiple Pages.
// Receives a pendingToken (JWT from backend) + projectId via URL params.
// User picks one page → POST /projects/:id/meta-account/select-page → done.

import React, { useState } from "react";
import { Loader2, Facebook, CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../atoms";
import { crmApi } from "../api/client";

function useSelectMetaPage(projectId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ pid, pendingToken, pageId }) =>
      (await crmApi.post(`/projects/${projectId}/meta-account/select-page`, { pid, pendingToken, pageId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crm", "project-meta-account", projectId] });
    },
  });
}

export default function MetaPagePickerModal({ projectId, pid, pendingToken, pages, onDone }) {
  const [selected, setSelected] = useState(null);
  const mut = useSelectMetaPage(projectId);

  const handleConfirm = async () => {
    if (!selected) return;
    try {
      const result = await mut.mutateAsync({ pid, pendingToken, pageId: selected.id });
      toast.success(`Facebook Page connected: ${result.pageName || selected.name}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Failed to save page");
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(0,0,0,0.45)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }}>
      <div style={{
        background: "var(--color-bg-surface)",
        border: "1px solid var(--color-border-subtle)",
        borderRadius: 8,
        width: "100%",
        maxWidth: 420,
        maxHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
        overflow: "hidden",
      }}>
        {/* Fixed header */}
        <div style={{ padding: "20px 20px 0" }}>
          <header style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <Facebook size={18} style={{ color: "#1877F2" }} />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Select a Facebook Page</h2>
          </header>
          <p style={{ margin: "6px 0 14px", fontSize: 13, color: "var(--color-text-secondary)" }}>
            Choose which page's Lead Ads should sync into this project.
          </p>
        </div>

        {/* Scrollable list */}
        <div style={{
          flex: 1, overflowY: "auto", padding: "0 20px",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {pages.map((page) => {
            const isSelected = selected?.id === page.id;
            return (
              <button
                key={page.id}
                onClick={() => setSelected(page)}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px",
                  border: `2px solid ${isSelected ? "var(--crm-primary)" : "var(--color-border-subtle)"}`,
                  borderRadius: 8,
                  background: isSelected ? "var(--crm-primary-soft)" : "var(--color-bg-base)",
                  cursor: "pointer", textAlign: "left",
                  transition: "border-color 0.15s, background 0.15s",
                  flexShrink: 0,
                }}
              >
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: "#1877F2",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Facebook size={14} color="#fff" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {page.name}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
                    ID: {page.id}
                  </div>
                </div>
                {isSelected && <CheckCircle2 size={15} style={{ color: "var(--crm-primary)", flexShrink: 0 }} />}
              </button>
            );
          })}
          <div style={{ height: 8 }} />
        </div>

        {/* Fixed footer */}
        <div style={{
          padding: "14px 20px",
          borderTop: "1px solid var(--color-border-subtle)",
          display: "flex", gap: 8, justifyContent: "flex-end",
          background: "var(--color-bg-surface)",
        }}>
          <Button variant="secondary" onClick={onDone} disabled={mut.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!selected || mut.isPending}
            leftIcon={mut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Facebook size={13} />}
          >
            {mut.isPending ? "Connecting…" : "Connect Page"}
          </Button>
        </div>
      </div>
    </div>
  );
}
